import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { Tooltip } from "../../../utils/local-antd";
import React, { useMemo, useState } from "react";
import {
  Controller,
  UseFieldArrayRemove,
  useFormContext,
} from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { FontSize, Text } from "../../../components/Text";
import { Textarea } from "../../../components/Textarea";
import { CreateCampaignDripDto } from "../../../model/campaign/dto/CreateCampaignDripDto";
import { UpdateCampaignDripDto } from "../../../model/campaign/dto/UpdateCampaignDripDto";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { DripCampaignPreview } from "./DripCampaignPreview";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { Info } from "../../../components/Info";
import { IconSelectModal } from "../../../components/IconSelectModal";

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xxl};
  padding: ${Spacing.m};
  border-radius: ${Rounded.lg};
  border: ${({ theme }) => `2px solid ${theme.darkerSub}`}55;
`;

const StyledInput = styled(Input)`
  border-radius: 0;
  border-color: transparent !important;
  border-bottom-color: ${({ theme, valid }) =>
    theme[valid ? "darkerSub" : "danger"]} !important;
  background-color: transparent !important;
  font-size: ${FontSize.m};
`;

const StyledTextarea = styled(Textarea)`
  border-radius: 0;
  border-color: transparent !important;
  border-bottom-color: ${({ theme, valid }) =>
    theme[valid ? "darkerSub" : "danger"]} !important;
  resize: vertical;
  width: 100%;
  background-color: transparent !important;
`;

const ActionsWrapper = styled(Flex)`
  align-items: center;
  gap: ${Spacing.m};
  border-top: ${({ theme }) => `2px solid ${theme.darkerSub}`}55;
  width: calc(100% + (${Spacing.m} * 2));
  margin: -${Spacing.m};
  padding: ${Spacing.m};
  justify-content: flex-end;
  background-color: ${({ theme }) => theme.sub}55;
  margin-top: ${Spacing.s};
`;

const StyledButton = styled(Button)`
  background-color: transparent;
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.main};
  font-size: ${FontSize.m};
`;

const SelectWrapper = styled(Flex)<{ valid: boolean }>`
  width: 100%;

  p:first-child {
    color: ${({ theme }) => theme.darkerSub};
    margin-top: ${Spacing.xs};
    padding-inline-start: ${Spacing.s};
  }

  .ant-select-selector {
    border-radius: 0;
    border-color: transparent !important;
    border-bottom-color: ${({ theme, valid }) =>
      theme[valid ? "darkerSub" : "danger"]} !important;
    box-shadow: none !important;
  }

  .ant-select-arrow svg {
    color: ${({ theme }) => theme.darkerSub} !important;
  }

  .ant-select-selector:hover {
    border-color: transparent !important;
    border-bottom-color: ${({ theme, valid }) =>
      theme[valid ? "darkerSub" : "danger"]} !important;
  }
`;

type DripCampaignStepProps = {
  index: number;
  remove: UseFieldArrayRemove;
};

export const DripCampaignStep: React.FC<DripCampaignStepProps> = ({
  index,
  remove,
}) => {
  const [iconsModalVisible, setIconsModalVisible] = useState(false);

  const { t } = useTranslation();
  const { control, watch, resetField, clearErrors, setValue } = useFormContext<
    CreateCampaignDripDto | UpdateCampaignDripDto
  >();

  const steps = watch("steps") || [];
  const previousStepDelay = index === 0 ? 0 : steps[index - 1]?.hoursAfter;
  const currentStepDelay = steps[index]?.hoursAfter;
  const nextStepDelay = steps[index + 1]?.hoursAfter;

  const dayOptions = useMemo(() => {
    const baseOptions = [
      1,
      2,
      6,
      12,
      1 * 24,
      2 * 24,
      3 * 24,
      5 * 24,
      7 * 24,
      14 * 24,
      30 * 24,
      60 * 24,
    ].filter((d) => d > previousStepDelay);

    const options =
      currentStepDelay != null && !baseOptions.includes(currentStepDelay)
        ? [currentStepDelay, ...baseOptions]
        : baseOptions;

    return options
      .filter((d) => d)
      .map((d) => {
        const label =
          d >= 24
            ? t(
                `campaigns.dripCampaign.step.campaignSteps.step.daysAfter.option.title${d / 24 > 1 && d / 24 < 9 ? "" : ".singular"}`,
                { days: d / 24 },
              )
            : t(
                `campaigns.dripCampaign.step.campaignSteps.step.hoursAfter.option.title${d > 1 && d < 9 ? "" : ".singular"}`,
                { hours: d },
              );

        return { label: label, value: d };
      });
  }, [t, previousStepDelay, currentStepDelay]);

  return (
    <Flex width="100%" gap="l">
      <Flex flexDirection="column" gap="s" width="50%">
        <Flex alignItems="center" gap="xs">
          <AtiraIcon icon={faGear} />
          <Text fontWeight={"bold"}>
            <Trans
              i18nKey={"campaigns.dripCampaign.step.campaignSteps.step.title"}
              values={{ step: index + 1 }}
            />
          </Text>
        </Flex>

        <Container>
          <Flex flexDirection="column" gap="l">
            <Controller
              control={control}
              name={`steps.${index}.subject`}
              rules={{
                required: {
                  value: true,
                  message: t("common.this_field_required"),
                },
                maxLength: {
                  value: Lengths.DESCRIPTION,
                  message: t("error.length.less", {
                    name: t(
                      "campaigns.dripCampaign.step.campaignSteps.step.title",
                    ).toLowerCase(),
                    length: Lengths.DESCRIPTION,
                  }),
                },
                minLength: {
                  value: Lengths.MIN_3,
                  message: t("error.length.more", {
                    name: t(
                      "campaigns.dripCampaign.step.campaignSteps.step.title",
                    ).toLowerCase(),
                    length: Lengths.MIN_3,
                  }),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Flex alignItems="center" gap="xs">
                  <StyledInput
                    placeholder={t(
                      "campaigns.dripCampaign.step.campaignSteps.step.title.input.placeholder",
                    )}
                    title={t(
                      "campaigns.dripCampaign.step.campaignSteps.step.title.input.title",
                    )}
                    value={value}
                    required
                    onChange={onChange}
                    valid={!error}
                    errorMessage={error?.message}
                    maxLength={Lengths.DESCRIPTION}
                  />
                </Flex>
              )}
            />

            <Controller
              control={control}
              name={`steps.${index}.body`}
              rules={{
                required: {
                  value: true,
                  message: t("common.this_field_required"),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <StyledTextarea
                  placeholder={t(
                    "campaigns.dripCampaign.step.campaignSteps.step.body.input.placeholder",
                  )}
                  title={t(
                    "campaigns.dripCampaign.step.campaignSteps.step.body.input.title",
                  )}
                  required
                  value={value}
                  onChange={onChange}
                  valid={!error}
                  errorMessage={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name={`steps.${index}.hoursAfter`}
              rules={{
                required: {
                  value: true,
                  message: t("common.this_field_required"),
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <SelectWrapper valid={!error}>
                  <AtiraSelect
                    options={dayOptions}
                    value={
                      value ? dayOptions?.find((o) => o.value === value) : null
                    }
                    onChange={(v) => {
                      onChange(v || null);
                      if (
                        nextStepDelay != null &&
                        (v == null || Number(v) >= nextStepDelay)
                      ) {
                        resetField(`steps.${index + 1}.hoursAfter`);
                      }
                    }}
                    valid={!error}
                    errorMessage={error?.message}
                    title={t(
                      `campaigns.dripCampaign.step.campaignSteps.step.daysAfter.input.title`,
                    )}
                    placeholder={t(
                      `campaigns.dripCampaign.step.campaignSteps.step.daysAfter.input.placeholder`,
                    )}
                    info={t(
                      `campaigns.dripCampaign.step.campaignSteps.step.daysAfter.info`,
                    )}
                    containerStyle={{ width: "100%" }}
                    allowClear
                  />
                </SelectWrapper>
              )}
            />
          </Flex>

          <Flex flexDirection="column" gap="xl">
            <Info
              info={t(
                "campaigns.dripCampaign.step.campaignSteps.step.cta.info",
              )}
            />

            <Controller
              control={control}
              name={`steps.${index}.ctaText`}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  placeholder={t(
                    "campaigns.dripCampaign.step.campaignSteps.step.ctaText.input.placeholder",
                  )}
                  title={t(
                    "campaigns.dripCampaign.step.campaignSteps.step.ctaText.input.title",
                  )}
                  value={value}
                  onChange={onChange}
                  valid={!error}
                  errorMessage={error?.message}
                  maxLength={Lengths.DESCRIPTION}
                />
              )}
            />

            <Controller
              control={control}
              name={`steps.${index}.ctaUrl`}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  placeholder={t(
                    "campaigns.dripCampaign.step.campaignSteps.step.ctaUrl.input.placeholder",
                  )}
                  title={t(
                    "campaigns.dripCampaign.step.campaignSteps.step.ctaUrl.input.title",
                  )}
                  value={value}
                  onChange={onChange}
                  valid={!error}
                  errorMessage={error?.message}
                  maxLength={Lengths.LONG}
                />
              )}
            />

            {/* <Controller
              control={control}
              name={`steps.${index}.ctaIcon`}
              render={({ field: { value } }) => (
                <Flex alignItems="flex-start" gap="s" flexDirection="column">
                  <Text>
                    {t(
                      "campaigns.dripCampaign.step.campaignSteps.step.ctaIcon.input.title",
                    )}
                  </Text>

                  <IconsButton
                    icon={getFontAwesomeIcon(value as any) || faExternalLink}
                    onClick={() => setIconsModalVisible(true)}
                  >
                    {t("common.choose")}
                  </IconsButton>
                </Flex>
              )}
            /> */}
          </Flex>

          <ActionsWrapper>
            <Tooltip
              title={t("campaigns.dripCampaign.step.campaignSteps.step.remove")}
            >
              <StyledButton
                disabled={index === 0}
                icon={faTrash}
                onClick={() => {
                  remove(index);
                  clearErrors(`steps.${index}`);
                }}
              />
            </Tooltip>
          </ActionsWrapper>
        </Container>
      </Flex>

      <Flex width="50%" alignItems="flex-start" justifyContent="center">
        <DripCampaignPreview step={steps[index]} />
      </Flex>

      <IconSelectModal
        open={iconsModalVisible}
        onClose={() => setIconsModalVisible(false)}
        onChange={(value) => {
          setValue(`steps.${index}.ctaIcon`, value);
        }}
        currentIcon={steps[index]?.ctaIcon as any}
      />
    </Flex>
  );
};
